import { http } from "../../utils/config";

const groupCustomerAPI = {
  // Get all with pagination and search
  getGroupCustomers: (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = "",
      orgId = '',
      sort_by = "created_at",
      sort_order = "desc",
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
      ...(orgId && { orgId }),
    });

    return http.get(`/api/v1/group-customers?${queryParams}`);
  },

  // Get by ID
  getGroupCustomerById: (id) => {
    return http.get(`/api/v1/group-customers/${id}`);
  },

  // Create new
  createGroupCustomer: (groupCustomerData) => {
    return http.post("/api/v1/group-customers", groupCustomerData);
  },

  // Update
  updateGroupCustomer: (id, groupCustomerData) => {
    return http.put(`/api/v1/group-customers/${id}`, groupCustomerData);
  },

  // Delete
  deleteGroupCustomer: (id) => {
    return http.delete(`/api/v1/group-customers/${id}`);
  },

  // them khach hang vao he thong
  attachCustomers: (id, customerData) => {
    return http.post(
      `/api/v1/group-customers/${id}/customers/attach`,
      customerData
    );
  },

  // go khach hang vao he thong
  detachCustomers: (id, customerData) => {
    return http.post(
      `/api/v1/group-customers/${id}/customers/detach`,
      customerData
    );
  },

  getCustomerByGroup: (id, params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = "",
      sort_by = "created_at",
      sort_order = "desc",
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
    });

    return http.get(
      `/api/v1/group-customers/${id}/get-customer-by-group?${queryParams}`
    );
  },

  getTotalCollections: (id) => {
    return http.get(`/api/v1/group-customers/${id}/total-collections`);
  },

  getGroupCollections: (id, params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = "",
      sort_by = "created_at",
      sort_order = "desc",
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
    });

    return http.get(`/api/v1/group-customers/${id}/collections?${queryParams}`);
  },

  getGroupAllByOrg: (id) => {
    return http.get(`/api/v1/group-customers/${id}/get-group-by-organization`);
  },

  // Toàn bộ nhóm trong phạm vi quyền của người dùng (không phụ thuộc đơn vị).
  getGroupAll: () => {
    return http.get(`/api/v1/group-customers/all`);
  },

  getCustomerAllByGroup: (id) => {
    return http.get(`/api/v1/group-customers/${id}/customer-all`);
  },
};

// Helper functions for handling API responses
export const GroupCustomerHelpers = {
  // Extract  data from API response
  extractGroupCustomerData: (response) => {
    if (response.data && response.success) {
      return response.data;
    }
    throw new Error(response.data?.message || "Unknown error occurred");
  },

  // Extract paginated list from API response
  extractGroupCustomerList: (response) => {
    if (response.data && response.success) {
      const data = response.data;

      return {
        groups: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url,
        },
      };
    }
    throw new Error(response.data?.message || "Unknown error occurred");
  },

  //   // Format data for form submission
  formatGroupCustomerData: (formData) => {
    return {
      organization_id: formData.organization_id ?? null,
      name: formData.name?.trim(),
      description: formData.description?.trim(),
    };
  },

  // Validate data before submission
  validateGroupCustomerData: (groupCustomerData) => {
    const errors = {};

    if (!groupCustomerData.name?.trim()) {
      errors.name = "Tên nhóm khách hàng không được để trống";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || "Dữ liệu không hợp lệ");
          case 401:
            throw new Error("Bạn cần đăng nhập để thực hiện thao tác này");
          case 403:
            throw new Error("Bạn không có quyền thực hiện thao tác này");
          case 404:
            throw new Error("Không tìm thấy khách hàng");
          case 422:
            throw new Error(data.message || "Dữ liệu không hợp lệ");
          case 500:
            throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default:
            throw new Error(data.message || "Đã có lỗi xảy ra");
        }
      } else if (error.request) {
        // Network error
        throw new Error("Không thể kết nối đến server");
      } else {
        // Other error
        throw new Error(error.message || "Đã có lỗi xảy ra");
      }
    }
  };
};

// Wrapped API functions with error handling
export const groupCustomerAPIWithErrorHandling = {
  getGroupCustomers: withErrorHandling(groupCustomerAPI.getGroupCustomers),
  getGroupCustomerById: withErrorHandling(
    groupCustomerAPI.getGroupCustomerById
  ),
  createGroupCustomer: withErrorHandling(groupCustomerAPI.createGroupCustomer),
  updateGroupCustomer: withErrorHandling(groupCustomerAPI.updateGroupCustomer),
  deleteGroupCustomer: withErrorHandling(groupCustomerAPI.deleteGroupCustomer),
  getCustomerByGroup: withErrorHandling(groupCustomerAPI.getCustomerByGroup),
  getTotalCollections: withErrorHandling(groupCustomerAPI.getTotalCollections),
  getGroupCollections: withErrorHandling(groupCustomerAPI.getGroupCollections),
  attachCustomers: withErrorHandling(groupCustomerAPI.attachCustomers),
  detachCustomers: withErrorHandling(groupCustomerAPI.detachCustomers),
  getGroupAllByOrg: withErrorHandling(groupCustomerAPI.getGroupAllByOrg),
  getGroupAll: withErrorHandling(groupCustomerAPI.getGroupAll),
  getCustomerAllByGroup: withErrorHandling(groupCustomerAPI.getCustomerAllByGroup),
};

export default groupCustomerAPI;
